import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Car, Gauge, Calendar, Palette, Engine, DoorOpen, Users, CheckCircle } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CarDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [car, setCar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const [showReservation, setShowReservation] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        telephone: '',
        email: ''
    });

    useEffect(() => {
        fetchCar();
    }, [id]);

    const fetchCar = async () => {
        try {
            const response = await axios.get(`${API}/cars/${id}`);
            setCar(response.data);
        } catch (error) {
            console.error('Error fetching car:', error);
            toast.error('Véhicule non trouvé');
            navigate('/voitures');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleReservation = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/orders/cars`, {
                car_id: car.id,
                client: formData
            });
            setOrderSuccess(true);
        } catch (error) {
            console.error('Error creating order:', error);
            toast.error('Erreur lors de la réservation');
        } finally {
            setSubmitting(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF' }).format(price);
    };

    const formatKm = (km) => {
        return new Intl.NumberFormat('fr-SN').format(km);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mmd-gold"></div>
            </div>
        );
    }

    if (!car) return null;

    const specs = [
        { icon: Calendar, label: 'Année', value: car.annee },
        { icon: Gauge, label: 'Kilométrage', value: `${formatKm(car.kilometrage)} km` },
        { icon: Palette, label: 'Couleur', value: car.couleur },
        { icon: Engine, label: 'Puissance', value: `${car.puissance_ch} ch` },
        { icon: DoorOpen, label: 'Portes', value: car.nb_portes },
        { icon: Users, label: 'Places', value: car.nb_places },
    ];

    return (
        <div data-testid="car-detail-page" className={`min-h-screen ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/voitures')}
                    data-testid="back-button"
                    className={`inline-flex items-center gap-2 mb-8 text-sm font-medium transition-colors ${
                        theme === 'dark' ? 'text-mmd-silver hover:text-mmd-gold' : 'text-mmd-navy/70 hover:text-mmd-navy'
                    }`}
                >
                    <ArrowLeft size={18} />
                    Retour au catalogue
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Image Gallery */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <div className={`rounded-2xl overflow-hidden ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`}>
                            <div className="aspect-[16/10]">
                                <img
                                    src={car.images?.[selectedImage] || 'https://via.placeholder.com/800x500?text=No+Image'}
                                    alt={`${car.marque} ${car.modele}`}
                                    className="w-full h-full object-cover"
                                    data-testid="main-car-image"
                                />
                            </div>
                        </div>
                        {car.images?.length > 1 && (
                            <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                                {car.images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedImage(idx)}
                                        className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                                            selectedImage === idx
                                                ? theme === 'dark' ? 'border-mmd-gold' : 'border-mmd-navy'
                                                : 'border-transparent'
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Car Info */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex flex-col"
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <p className={`text-sm font-medium uppercase tracking-wide ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                                {car.marque}
                            </p>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                theme === 'dark' ? 'bg-mmd-gold/20 text-mmd-gold' : 'bg-mmd-navy/10 text-mmd-navy'
                            }`}>
                                {car.type_vehicule}
                            </span>
                        </div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mb-4" data-testid="car-name">
                            {car.modele}
                        </h1>
                        
                        <span className={`text-3xl font-bold mb-8 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`} data-testid="car-price">
                            {formatPrice(car.prix)}
                        </span>

                        {/* Specs Grid */}
                        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-4 p-6 rounded-xl mb-8 ${
                            theme === 'dark' ? 'bg-mmd-surface-dark border border-white/10' : 'bg-white border border-black/5'
                        }`}>
                            {specs.map((spec, idx) => (
                                <div key={idx} className="text-center">
                                    <spec.icon size={24} className={`mx-auto mb-2 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`} />
                                    <p className="text-xs text-muted-foreground mb-1">{spec.label}</p>
                                    <p className="font-medium">{spec.value}</p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-auto">
                            <Button
                                onClick={() => setShowReservation(true)}
                                data-testid="reserve-car-button"
                                className={`w-full sm:w-auto px-8 py-6 text-lg font-medium ${
                                    theme === 'dark'
                                        ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover'
                                        : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'
                                }`}
                            >
                                <Car size={20} className="mr-2" />
                                Réserver ce véhicule
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Reservation Dialog */}
            <Dialog open={showReservation} onOpenChange={setShowReservation}>
                <DialogContent className="sm:max-w-md">
                    {orderSuccess ? (
                        <div className="text-center py-8">
                            <CheckCircle size={64} className="mx-auto mb-4 text-green-500" weight="fill" />
                            <h3 className="font-heading text-xl font-medium mb-2">Commande bien enregistrée !</h3>
                            <p className="text-muted-foreground mb-6">
                                Vous serez contacté prochainement pour finaliser votre réservation.
                            </p>
                            <Button onClick={() => { setShowReservation(false); setOrderSuccess(false); }}>
                                Fermer
                            </Button>
                        </div>
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle>Réserver {car.marque} {car.modele}</DialogTitle>
                                <DialogDescription>
                                    Remplissez vos coordonnées pour réserver ce véhicule.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleReservation} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="nom">Nom</Label>
                                        <Input
                                            id="nom"
                                            name="nom"
                                            value={formData.nom}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="car-reservation-nom"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="prenom">Prénom</Label>
                                        <Input
                                            id="prenom"
                                            name="prenom"
                                            value={formData.prenom}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="car-reservation-prenom"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="telephone">Téléphone</Label>
                                    <Input
                                        id="telephone"
                                        name="telephone"
                                        type="tel"
                                        value={formData.telephone}
                                        onChange={handleInputChange}
                                        required
                                        data-testid="car-reservation-telephone"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        data-testid="car-reservation-email"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={submitting}
                                    data-testid="submit-car-reservation"
                                >
                                    {submitting ? 'Envoi en cours...' : 'Confirmer la réservation'}
                                </Button>
                            </form>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
